import React from 'react';
import cn from 'classnames';

const MIN_HEIGHT = 303;

export const VerticalResizeHandle = ({
  children,
  onStopResize,
  row,
  initialHeight,
  chartId,
}: {
  children: React.ReactNode;
  onStopResize: (height: number, row: number, chartId: string) => void;
  row: number;
  initialHeight: number | undefined;
  chartId: string;
}) => {
  const resizableElRef = React.useRef<HTMLDivElement>(null);
  const handleRef = React.useRef<HTMLDivElement>(null);

  const [height, setHeight] = React.useState(initialHeight);
  const [isResizing, setIsResizing] = React.useState(false);

  React.useEffect(() => {
    if (resizableElRef && height === 0) {
      setHeight(resizableElRef.current?.clientHeight ?? 0);
    }
  }, [height, resizableElRef]);

  React.useEffect(() => {
    if (!isResizing && height !== undefined && height !== initialHeight) {
      onStopResize(height, row, chartId);
    }
  }, [isResizing, height, row, onStopResize, initialHeight, chartId]);

  React.useEffect(() => {
    // Add vertical drag to resize the element
    if (!handleRef.current) return;

    let startY = 0;
    let startHeight = 0;

    const onMouseMove = (e: MouseEvent) => {
      const deltaY = e.clientY - startY;
      const newHeight = Math.max(MIN_HEIGHT, startHeight + deltaY);
      setHeight(newHeight);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setIsResizing(false);
    };

    const onMouseDown = (e: MouseEvent) => {
      startY = e.clientY;
      startHeight = resizableElRef.current?.clientHeight ?? 0;
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      setIsResizing(true);
      // Prevent text selection while dragging
      e.preventDefault();
    };

    const handle = handleRef.current;
    handle.addEventListener('mousedown', onMouseDown);

    // Cleanup
    return () => {
      handle.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onStopResize, resizableElRef]);

  return (
    <>
      <div
        className="flex flex-col min-h-[303px]"
        ref={resizableElRef}
        style={{ height: height !== 0 ? `${height}px` : 'auto' }}
      >
        {children}
      </div>
      <div
        ref={handleRef}
        className={cn(
          'relative h-1 my-1 rounded-md bg-blue-500 mx-5 cursor-row-resize opacity-0 hover:opacity-100 transition-opacity duration-100',
          {
            'opacity-100': isResizing,
          },
        )}
      ></div>
    </>
  );
};
